
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarIcon, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { createLocalDateTime } from "@/utils/timeUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface EditBlockDialogProps {
  serviceName: string;
  selectedDate: Date | undefined;
  selectedStartTime: string;
  selectedEndTime: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (startTime: string, endTime: string) => Promise<void>;
  onDelete: () => void;
  loading?: boolean;
  serviceId?: string | number;
  blockingId?: number;
}

export const EditBlockDialog = ({ 
  serviceName, 
  selectedDate,
  selectedStartTime,
  selectedEndTime,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  loading = false,
  serviceId,
  blockingId,
}: EditBlockDialogProps) => {
  const [startTime, setStartTime] = useState(selectedStartTime);
  const [endTime, setEndTime] = useState(selectedEndTime);
  const [conflictError, setConflictError] = useState<string | null>(null);

  useEffect(() => {
    setStartTime(selectedStartTime);
    setEndTime(selectedEndTime);
  }, [selectedStartTime, selectedEndTime]);

  useEffect(() => {
    // Clear conflict error when time selection changes
    if (startTime && endTime && selectedDate && serviceId) {
      checkForTimeConflicts(startTime, endTime);
    } else {
      setConflictError(null);
    }
  }, [startTime, endTime]);

  const checkForTimeConflicts = async (start: string, end: string) => {
    if (!selectedDate || !start || !end || !serviceId) return;
    
    try {
      // Simple local date/time approach - no timezone conversion
      const { date: startDateStr, time: startTimeStr } = createLocalDateTime(selectedDate, start);
      const { date: endDateStr, time: endTimeStr } = createLocalDateTime(selectedDate, end);
      
      console.log(`🔍 Edit block conflict check: ${startDateStr} ${startTimeStr} - ${endTimeStr} (local)`);
      
      // Check for overlapping bookings, excluding the current block being edited
      let query = supabase
        .from('bookings')
        .select('*')
        .eq('service_id', typeof serviceId === 'string' ? parseInt(serviceId) : serviceId);
        
      // Try querying using new columns first, fallback to old columns
      query = query.or(`and(booking_date.eq.${startDateStr},start_time_local.lt.${endTimeStr},end_time_local.gt.${startTimeStr}),and(start_time.lt.${endDateStr} ${endTimeStr.replace(':', '\\:')},end_time.gt.${startDateStr} ${startTimeStr.replace(':', '\\:')})`);
      // Exclude the current block being edited
      if (blockingId) {
        query = query.neq('id', blockingId);
      }
      
      const { data: conflictingBookings, error } = await query;
      
      if (error) throw error;
      
      if (conflictingBookings && conflictingBookings.length > 0) {
        setConflictError('This time slot conflicts with an existing booking or blocked time. Please select a different time.');
      } else {
        setConflictError(null);
      }
    } catch (error) {
      console.error('Error checking for time conflicts:', error);
      setConflictError(null);
    }
  };

  const timeSlots = [];
  for (let hour = 7; hour <= 19; hour++) {
    const formattedHour = hour.toString().padStart(2, '0');
    timeSlots.push(`${formattedHour}:00`);
    if (hour < 19) {
      timeSlots.push(`${formattedHour}:30`);
    }
  }

  const availableEndTimes = startTime 
    ? timeSlots.slice(timeSlots.indexOf(startTime) + 1)
    : [];

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleUpdate = () => {
    if (startTime && endTime && !conflictError) {
      onUpdate(startTime, endTime);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Modify Block - {serviceName}</DialogTitle>
          <DialogDescription>
            Modify the time slot duration. This will update the blocked period.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="space-y-4">
            <p className="text-sm font-medium text-gray-900">
              Time slot to modify:
            </p>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="font-medium">{selectedDate && format(selectedDate, "MMMM d, yyyy")}</p>
              
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900">Start Time</label>
                  <Select value={startTime || undefined} onValueChange={setStartTime}>
                    <SelectTrigger className="w-full bg-white border-2 border-gray-300">
                      <SelectValue placeholder="Select start time" />
                    </SelectTrigger>
                    <SelectContent className="z-[100] bg-white">
                      {timeSlots.map((time) => (
                        <SelectItem key={time} value={time} className="cursor-pointer">
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900">End Time</label>
                  <Select value={endTime || undefined} onValueChange={setEndTime} disabled={!startTime}>
                    <SelectTrigger className="w-full bg-white border-2 border-gray-300">
                      <SelectValue placeholder="Select end time" />
                    </SelectTrigger>
                    <SelectContent className="z-[100] bg-white">
                      {availableEndTimes.map((time) => (
                        <SelectItem key={time} value={time} className="cursor-pointer">
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {conflictError && (
                <Alert variant="destructive" className="mt-4">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  <AlertDescription>{conflictError}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={handleUpdate}
              disabled={loading || !startTime || !endTime || !!conflictError}
              className="flex-1"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {loading ? 'Modifying...' : 'Modify Block'}
            </Button>
            
            <Button 
              onClick={onDelete}
              variant="destructive"
              disabled={loading}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Block
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
